import React from 'react';
import { StepProps } from '../MultiStepModal';

const JOB_TYPE_LABELS: Record<string, string> = {
  EMERGENCY: '🚨 Emergency callout',
  MAINTENANCE: '🔧 Maintenance visit',
  INSTALLATION: '🏗️ Installation',
  INSPECTION: '📋 Inspection / compliance',
  REPAIR: '🔩 Repair',
  OTHER: '📁 Other',
};

const PRIORITY_COLORS: Record<string, string> = {
  LOW: 'text-gray-600 bg-gray-50 border-gray-200',
  MEDIUM: 'text-blue-700 bg-blue-50 border-blue-200',
  HIGH: 'text-orange-700 bg-orange-50 border-orange-200',
  URGENT: 'text-red-700 bg-red-50 border-red-200',
};

function Row({ label, value }: { label: string; value?: string | number }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="text-[11px] text-[#6B7280] flex-shrink-0 w-28">{label}</span>
      <span className="text-[12px] font-medium text-[#191919] text-right">{value}</span>
    </div>
  );
}

export default function JobConfirmStep({ data }: StepProps) {
  const scheduledLabel = data.scheduledDate && data.scheduledTime
    ? `${new Date(`${data.scheduledDate}T${data.scheduledTime}`).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
    : 'Not set';

  const revenue = data.estimatedRevenue || 0;
  const cost = data.estimatedCost || 0;
  const margin = revenue - cost;

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Confirm & create</h3>
        <p className="step-form-subtitle">Review everything before creating the job. The system will assign it, notify the engineer, and add it to the queue.</p>
      </div>

      <div className="space-y-3">
        {/* Job card */}
        <div className="rounded-xl border border-[#E6E6E4] bg-[#FAFAFA] divide-y divide-[#F3F4F6] overflow-hidden">
          <div className="px-4 py-2.5 bg-white">
            <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Job</p>
            <Row label="Title" value={data.title} />
            <Row label="Type" value={JOB_TYPE_LABELS[data.jobType] || data.jobType} />
            <Row label="Priority" value={data.priority} />
            <Row label="Duration" value={data.estimatedDuration ? `${data.estimatedDuration} mins` : undefined} />
          </div>

          <div className="px-4 py-2.5 bg-white">
            <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Client & site</p>
            <Row label="Client" value={data.customerName} />
            <Row label="Address" value={data.customerAddress || data.siteAddress} />
            <Row label="Postcode" value={data.postcode || data.sitePostcode} />
            <Row label="Site" value={data.siteName || (data.isNewSite ? 'New site' : undefined)} />
          </div>

          <div className="px-4 py-2.5 bg-white">
            <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Schedule</p>
            <Row label="Date & time" value={scheduledLabel} />
            <Row label="Engineer" value={data.engineerName || 'Unassigned'} />
          </div>

          {(revenue > 0 || cost > 0) && (
            <div className="px-4 py-2.5 bg-white">
              <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Pricing</p>
              <Row label="Revenue" value={`£${revenue.toFixed(2)}`} />
              <Row label="Cost" value={`£${cost.toFixed(2)}`} />
              <Row label="Margin" value={`£${margin.toFixed(2)}`} />
            </div>
          )}
        </div>

        {/* Priority badge */}
        {data.priority && (
          <div className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium ${PRIORITY_COLORS[data.priority] || PRIORITY_COLORS['MEDIUM']}`}>
            Priority: {data.priority}
          </div>
        )}

        {/* What happens next */}
        <div className="rounded-xl border border-[#E6E6E4] bg-white px-4 py-3">
          <p className="text-[11px] font-semibold text-[#374151] mb-2">What happens after creation</p>
          <ul className="space-y-1">
            {[
              'Job status set to Scheduled',
              data.engineerId ? `Engineer "${data.engineerName}" notified` : 'Added to unassigned queue',
              'Job appears in Timeline, Queue, and Pipeline',
              'Lifecycle events start logging immediately',
            ].map((s, i) => (
              <li key={i} className="flex items-center gap-2 text-[11px] text-[#6B7280]">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                {s}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
